import { __awaiter } from "tslib";
import { BrowserCacheLocation, EventType, PublicClientApplication, } from '@azure/msal-browser';
import { ReplaySubject, Subject } from 'rxjs';
import { deleteCookie, filterApiScopes, FLAG_IS_IDP_REDIRECT, getLoginOriginFromEnvironment, getTenantIdFromEnvironment, isSewLocation, setSessionCookie, } from './msal-helper';
export class MsalAuthService {
    constructor(clientId, redirectUri, postLogoutRedirectUri, language, country, environment, apiScopes, policy) {
        this.tokenUpdatedSubject = new Subject();
        this.tokenFetchInitialSuccessSubject = new ReplaySubject(1);
        this.userUpdatedSubject = new Subject();
        this.acquireTokenConnections = [];
        this.queryParams = { country, language, origin: window.location.origin };
        policy = policy || 'B2C_1A_SEW_SUSI_HRD';
        const msalConfig = this.createMsalConfig(clientId, getTenantIdFromEnvironment(environment), redirectUri, postLogoutRedirectUri, policy, getLoginOriginFromEnvironment(environment));
        this.msalInstance = new PublicClientApplication(msalConfig);
        this.msalInstance.addEventCallback(event => {
            var _a;
            switch (event.eventType) {
                case EventType.LOGIN_SUCCESS:
                case EventType.SSO_SILENT_SUCCESS:
                    setSessionCookie('B2CSessionActive', 'X');
                    const authResult = event.payload;
                    this.msalInstance.setActiveAccount(authResult.account);
                    this.updateUserInfo(authResult.idTokenClaims);
                    this.tokenUpdatedSubject.next({
                        api: (authResult === null || authResult === void 0 ? void 0 : authResult.state) || '',
                        token: authResult.accessToken,
                    });
                    if (this.apiScopes.length > 1 && authResult.state) {
                        this.acquireTokenSilent(filterApiScopes(this.apiScopes, authResult.state), this.msalInstance.getAllAccounts()[0]);
                    }
                    break;
                case EventType.SSO_SILENT_FAILURE:
                    console.log('SSO Silent Failure:', event);
                    this.employeeLogin();
                    break;
                case EventType.ACQUIRE_TOKEN_START:
                    const startPayload = event.payload;
                    if (startPayload.correlationId) {
                        this.acquireTokenConnections.push({
                            correlationId: startPayload.correlationId,
                            api: startPayload.state || '',
                        });
                    }
                    break;
                case EventType.ACQUIRE_TOKEN_SUCCESS:
                    setSessionCookie('B2CSessionActive', 'X');
                    const authResultSilent = event.payload;
                    this.updateUserInfo(authResultSilent.idTokenClaims);
                    const currentAPI = (_a = this.acquireTokenConnections.find(entry => entry.correlationId === authResultSilent.correlationId)) === null || _a === void 0 ? void 0 : _a.api;
                    this.tokenUpdatedSubject.next({
                        api: currentAPI || '',
                        token: authResultSilent.accessToken,
                    });
                    this.acquireTokenConnections = this.acquireTokenConnections.filter(entry => entry.correlationId !== authResultSilent.correlationId);
                    break;
                case EventType.ACQUIRE_TOKEN_FAILURE:
                    this.employeeLogin();
                    break;
                case EventType.HANDLE_REDIRECT_END:
                    sessionStorage.removeItem(FLAG_IS_IDP_REDIRECT);
                    break;
                case EventType.LOGOUT_SUCCESS:
                    deleteCookie('B2CSessionActive');
                    this.updateUserInfo(null);
                    this.msalInstance.clearCache();
                    this.tokenUpdatedSubject.next(null);
                    this.tokenUpdatedSubject.complete();
                    break;
                default:
                    console.log('Unhandled Event:', event);
                    break;
            }
        });
        this.apiScopes = apiScopes;
    }
    init() {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.msalInstance.initialize();
            try {
                if (sessionStorage.getItem(FLAG_IS_IDP_REDIRECT)) {
                    yield this.msalInstance.handleRedirectPromise();
                    if (this.msalInstance.getAllAccounts().length === 0) {
                        this.userUpdatedSubject.next(null);
                    }
                }
                else {
                    if (this.msalInstance.getAllAccounts().length === 0) {
                        yield this.msalInstance
                            .ssoSilent({
                            scopes: this.apiScopes[0].scopes,
                            state: this.apiScopes[0].api,
                        })
                            .catch(error => {
                            error;
                        });
                    }
                    else if (this.msalInstance.getAllAccounts().length === 1) {
                        this.acquireTokenSilent(this.apiScopes, this.msalInstance.getAllAccounts()[0]);
                    }
                    else {
                        console.error('More than one account found.');
                    }
                }
            }
            catch (error) { }
        });
    }
    login() {
        sessionStorage.setItem(FLAG_IS_IDP_REDIRECT, 'true');
        this.msalInstance.loginRedirect({
            scopes: this.apiScopes[0].scopes,
            state: this.apiScopes[0].api,
            extraQueryParameters: this.queryParams,
        });
    }
    logout() {
        sessionStorage.setItem(FLAG_IS_IDP_REDIRECT, 'true');
        this.msalInstance.logoutRedirect();
    }
    getTokenForApi(api) {
        return __awaiter(this, void 0, void 0, function* () {
            const apiData = this.apiScopes.find(apiScope => apiScope.api === api);
            if (!apiData) {
                console.error('API not found in apiScopes:', api);
                return null;
            }
            try {
                const response = yield this.msalInstance.acquireTokenSilent({
                    scopes: apiData.scopes,
                    state: apiData.api,
                });
                return response.accessToken;
            }
            catch (error) {
                console.error('Error acquiring token:', error);
                return null;
            }
        });
    }
    get tokenUpdated$() {
        return this.tokenUpdatedSubject.asObservable();
    }
    get user$() {
        return this.userUpdatedSubject.asObservable();
    }
    get tokenFetchInitialSuccess$() {
        return this.tokenFetchInitialSuccessSubject.asObservable();
    }
    createMsalConfig(clientId, tenantId, redirectUri, postLogoutRedirectUri, policy, origin) {
        return {
            auth: {
                clientId,
                authority: `${origin}/${tenantId}/${policy}`,
                knownAuthorities: [origin],
                redirectUri,
                postLogoutRedirectUri,
                navigateToLoginRequestUrl: true,
            },
            cache: {
                cacheLocation: BrowserCacheLocation.LocalStorage,
                storeAuthStateInCookie: true,
                secureCookies: true,
            },
            system: {
                windowHashTimeout: 60000,
                iframeHashTimeout: 6000,
                asyncPopups: false,
                allowRedirectInIframe: true,
            },
        };
    }
    updateUserInfo(idTokenClaims) {
        if (!idTokenClaims) {
            this.userUpdatedSubject.next(null);
            return;
        }
        const sewUserClaims = {
            id: idTokenClaims.sub || undefined,
            displayName: idTokenClaims.displayName || undefined,
            firstName: idTokenClaims.given_name || idTokenClaims.firstName || undefined,
            lastName: idTokenClaims.family_name || idTokenClaims.lastName || undefined,
            mail: idTokenClaims.mail || undefined,
            contactId: idTokenClaims.contactId || undefined,
            country: idTokenClaims.country || undefined,
            language: idTokenClaims.language || undefined,
            sapUserId: idTokenClaims.userId || undefined,
            isEmployee: idTokenClaims.isEmployee || undefined,
            employee: idTokenClaims.isEmployee || undefined,
        };
        this.userUpdatedSubject.next(sewUserClaims);
    }
    acquireTokenSilent(apiScopes, account) {
        const promises = apiScopes.map(apiScope => this.msalInstance.acquireTokenSilent({
            scopes: apiScope.scopes,
            state: apiScope.api,
            account,
        }));
        Promise.all(promises)
            .then(() => {
            console.log('All tokens acquired successfully.');
            this.tokenFetchInitialSuccessSubject.next(true);
            this.tokenFetchInitialSuccessSubject.complete();
        })
            .catch(error => {
            console.error('Error acquiring one or more tokens:', error);
            this.tokenFetchInitialSuccessSubject.next(false);
            this.tokenFetchInitialSuccessSubject.complete();
        });
    }
    employeeLogin() {
        if (isSewLocation() && !localStorage.getItem('sso_disabled')) {
            localStorage.setItem('sso_disabled', 'true');
            this.msalInstance.loginPopup({
                scopes: this.apiScopes[0].scopes,
                state: this.apiScopes[0].api,
                extraQueryParameters: this.queryParams,
                domainHint: 'sew-eurodrive',
            });
        }
        else {
            this.updateUserInfo(null);
        }
    }
}
//# sourceMappingURL=msal-service.js.map